// The way into a park, as one branded screen (06-web-and-app: the loading screen). It covers every
// wait between a tap on the menu and the first frame of the street: the park office's answer, the
// round's screens arriving, the seat in the park, then the street's code, its bake and its first
// draw. Each moment builds the same screen, so the sequence reads as one: the lockup, the game type's
// tile with the park and its map, the player's own keke driving on the splash's road, the design
// system's route bar with one stop per real stage, the stage's line and a Pidgin tip that turns.
//
// Plain DOM (game/road.ts mounts it over the street; ParkLoading wraps it for React). Every loop is
// CSS (transform and opacity) phased to the page clock, so a screen built again for the next moment
// carries on where the last one was; prefers-reduced-motion holds one still frame.
import { PRESETS, type PresetName } from '@wahala/config';
import lockup from '../ds/wahala-lockup.svg';
import type { World } from '../store/world';
import { S } from '../strings';
import { W } from './wahala';

/** What the screen knows about the park being loaded; it fills in as the park answers. */
export interface ParkLoadInfo {
  /** The game type (a preset id: park, race, hide, powers, stakes4p, ...), once known. */
  type?: string | undefined;
  /** The park's own name, if the host gave it one. */
  name?: string | undefined;
  /** The park's code (never for Solo). */
  code?: string | undefined;
  /** The map's id (lagos-route, third-mainland). */
  map?: string | undefined;
  solo?: boolean | undefined;
  /** The player's colour in this park; the menu's danfo until the park deals one. */
  colour?: string | undefined;
  driver?: string | undefined;
  look?: { load?: string | undefined; finish?: string | undefined; slogan?: string | undefined } | undefined;
}

/** The two legs of the way in: the park (a seat) and the street (the road drawn). */
export type ParkLoadLeg = 'park' | 'street';
/** Each leg's real stages. Park: the office answers, the round's screens arrive, the seat is taken. */
const STAGES: Record<ParkLoadLeg, number> = { park: 3, street: 3 };

export interface ParkLoader {
  el: HTMLElement;
  /** A stage of the leg done: the bar ticks a stop and the line says what runs now. Never goes back. */
  reach(done: number): void;
  /** More is known about the park: the tile and the keke redraw. */
  set(info: ParkLoadInfo): void;
  /** Stop the bar's creep (the screen is going). */
  stop(): void;
  /** Fade out, then leave the page. */
  leave(): void;
}

/** One turn of the tips, and how many show in one cycle (styles.css .pl__tips keyframes use 6). */
const TIP_MS = 4_500;
const TIPS_SHOWN = 6;
/** Where this visit's tips start, so two rounds in a row don't open on the same line. */
const tipStart = Math.floor(Math.random() * 64);

/** What a park's world already says: its game, name and map, and this player's colour and look. */
export function worldInfo(world: World): ParkLoadInfo {
  const lobby = world.lobby;
  const me = lobby?.members.find((m) => m.id === world.you);
  const look = world.you ? world.looks.get(world.you) : undefined;
  const colour = me?.colour ?? world.view?.you.colour;
  return {
    ...(lobby ? { type: lobby.preset, map: world.mapId } : {}),
    ...(lobby?.parkName ? { name: lobby.parkName } : {}),
    ...(colour ? { colour } : {}),
    ...(me?.name ? { driver: me.name } : {}),
    ...(look ? { look } : {}),
  };
}

/** A park session's own facts (Solo, its code) over what the way in already knew and what its world says. */
export function sessionInfo(
  park: { code: string; practice: boolean; world: World },
  known: ParkLoadInfo | null = null,
): ParkLoadInfo {
  const solo = park.code === 'SOLO';
  return {
    ...(known ?? {}),
    ...(solo ? { solo: true } : {}),
    ...(!park.practice ? { code: park.code } : {}),
    ...worldInfo(park.world),
  };
}

/** The design system's game type tile for a preset. */
function tileFor(type: string | undefined): 'park' | 'race' | 'hide' | 'powers' | 'stake' | null {
  if (!type) return null;
  if (type === 'race' || type === 'hide' || type === 'powers') return type;
  if (type.startsWith('stakes')) return 'stake';
  return 'park';
}

/** The tips for this game type: its own three, then three for every park. */
export function tipsFor(type: string | undefined): string[] {
  const t = S.parkLoad.tips;
  const own = type === 'race' ? t.race : type === 'hide' ? t.hide : type === 'powers' ? t.powers : t.park;
  const all = [...own, ...t.any].slice(0, TIPS_SHOWN);
  const k = tipStart % all.length;
  return [...all.slice(k), ...all.slice(0, k)];
}

/** The tile's line: the park's name or code, and its map. */
export function placeLine(info: ParkLoadInfo): string {
  const mapId = info.map ?? (info.type ? PRESETS[info.type as PresetName]?.mapId : undefined);
  const map = mapId ? S.parkLoad.maps[mapId] : undefined;
  const where = info.solo ? S.parkLoad.solo : info.name || (info.code ? S.parkLoad.park(info.code) : undefined);
  return [where, map].filter(Boolean).join(' · ');
}

/** The line under the bar for a leg's stage (the stage now running). */
function lineFor(leg: ParkLoadLeg, done: number, solo: boolean): string {
  const l = S.parkLoad.lines;
  if (leg === 'park') return done === 0 ? (solo ? l.solo : l.find) : done === 1 ? l.gate : l.seat;
  return done === 0 ? l.street : done === 1 ? l.build : l.light;
}

export function parkLoading(first: ParkLoadInfo, leg: ParkLoadLeg): ParkLoader {
  let info = { ...first };
  const total = STAGES[leg];
  const el = document.createElement('div');
  el.className = 'pl';
  el.dataset.theme = 'night';
  el.dataset.leg = leg;
  el.setAttribute('role', 'status');
  // the loops run on the page clock: a screen built again later picks them up mid-stride
  el.style.setProperty('--pl-t', `-${Math.round(performance.now() % (TIP_MS * TIPS_SHOWN))}ms`);

  const top = document.createElement('div');
  top.className = 'pl__a';
  const logo = document.createElement('img');
  logo.className = 'pl__lockup';
  logo.src = lockup;
  logo.alt = S.appName;
  logo.decoding = 'async';
  const game = document.createElement('div');
  game.className = 'pl__game';
  const code = document.createElement('p');
  code.className = 'pl__code';
  top.append(logo, game, code);

  const bottom = document.createElement('div');
  bottom.className = 'pl__b';
  // the splash's road: the lane runs under the player's own keke while it waits
  const road = document.createElement('div');
  road.className = 'pl__road';
  road.setAttribute('aria-hidden', 'true');
  const lane = document.createElement('div');
  lane.className = 'pl__lane';
  const you = document.createElement('div');
  you.className = 'pl__you';
  road.append(lane, you);
  const bar = document.createElement('div');
  bar.className = 'pl__bar';
  const line = document.createElement('p');
  line.className = 'pl__line';
  line.setAttribute('aria-live', 'polite');
  const tips = document.createElement('div');
  tips.className = 'pl__tips';
  const tipLabel = document.createElement('span');
  tipLabel.className = 'pl__tip-label';
  tipLabel.textContent = S.parkLoad.tip;
  const tipList = document.createElement('div');
  tipList.className = 'pl__tip-list';
  tips.append(tipLabel, tipList);
  bottom.append(road, bar, line, tips);
  el.append(top, bottom);

  let shownType: string | undefined | null = null;
  const drawInfo = () => {
    const tile = tileFor(info.type);
    game.innerHTML = tile
      ? W().gameTypeTile({
          type: tile,
          button: false,
          title: S.presets[info.type ?? ''] ?? S.presets.park ?? '',
          line: placeLine(info) || S.parkLoad.lines.find,
        } as never)
      : '';
    game.hidden = !tile;
    // joined by code, its game not yet known: the code stands in for the tile
    code.textContent = !tile && info.code ? S.parkLoad.park(info.code) : '';
    code.hidden = !code.textContent;
    const colour = info.colour ?? 'danfo';
    const look = info.look;
    you.innerHTML =
      // identity is the colour, the plate and the name together (the colour's own name until signed in)
      W().nameTag({ player: colour as never, tone: 'me', ...(info.driver ? { name: info.driver } : {}) }) +
      W().keke({
        player: colour as never,
        state: 'go',
        size: 132,
        ...(look?.load ? { load: look.load as never } : {}),
        ...(look?.finish ? { finish: look.finish as never } : {}),
        ...(look?.slogan ? { slogan: look.slogan } : {}),
      });
    // the tips follow the game type: rebuilt only when it changes, so a turn in progress carries on
    if (shownType !== info.type) {
      shownType = info.type;
      tipList.replaceChildren(
        ...tipsFor(info.type).map((text, i) => {
          const p = document.createElement('p');
          p.textContent = text;
          p.style.setProperty('--i', String(i));
          return p;
        }),
      );
    }
  };

  let stage = 0;
  let pct = 0;
  const drawBar = () => {
    bar.innerHTML = W().routeBar({
      done: stage,
      total,
      label: leg === 'park' ? S.parkLoad.parkBar : S.play.streetBar,
      player: (info.colour ?? 'danfo') as never,
    });
    line.textContent = lineFor(leg, stage, info.solo === true);
    // reduced motion: the bar stands at the stages done, as the design system draws it
    if (!reduced) creep();
  };
  // most of the way to the next stop while a stage runs, slowing as it gets there: the bar never
  // stands still, never passes a stop before its stage is done and never goes back
  const creep = () => {
    const cap = ((Math.min(stage, total - 1) + 0.9) / total) * 100;
    pct = Math.max(pct, (stage / total) * 100);
    pct += Math.max(0, cap - pct) * 0.06;
    const fill = bar.querySelector<HTMLElement>('.wh-route__fill');
    const keke = bar.querySelector<HTMLElement>('.wh-route__keke');
    if (fill) fill.style.width = `${pct.toFixed(2)}%`;
    if (keke) {
      keke.style.left = `${pct.toFixed(2)}%`;
      // the keke rides behind the fill once there is room for it, never back over the label
      keke.classList.toggle('is-start', stage === 0 && pct < 12);
    }
  };
  const reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  drawInfo();
  drawBar();
  const timer = reduced ? null : setInterval(creep, 120);
  const stop = () => {
    if (timer) clearInterval(timer);
  };

  return {
    el,
    reach(n) {
      if (n <= stage) return;
      stage = Math.min(n, total);
      drawBar();
    },
    set(next) {
      const merged = { ...info, ...next };
      if (JSON.stringify(merged) === JSON.stringify(info)) return;
      info = merged;
      drawInfo();
      drawBar();
    },
    stop,
    leave() {
      stop();
      el.classList.add('is-gone');
      setTimeout(() => el.remove(), reduced ? 0 : 320);
    },
  };
}

/** The street's leg over the road, from the round's first moment until the street draws. */
export interface StreetWait {
  /** A stage of the street done: its code arrived (1), it is built (2). */
  reach(done: number): void;
  /** The street drew, or the round went: fade out and give the round its HUD back. */
  end(): void;
}

/**
 * Cover the road in `host` with the street's leg at once (before the road's own code has arrived):
 * under the role card and the race's start lights, with the HUD and the stick held back until the
 * street draws (styles.css, .road[data-waiting]).
 */
export function waitForStreet(host: HTMLElement, world: World, info: ParkLoadInfo): StreetWait {
  const l = parkLoading({ ...info, ...worldInfo(world) }, 'street');
  l.el.classList.add('road__wait');
  host.append(l.el);
  const round = host.closest<HTMLElement>('.road');
  round?.setAttribute('data-waiting', '');
  world.roadWaiting = true;
  let ended = false;
  return {
    reach: (n) => l.reach(n),
    end() {
      if (ended) return;
      ended = true;
      l.leave();
      world.roadWaiting = false;
      round?.removeAttribute('data-waiting');
    },
  };
}
